import { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { LoginFormik } from "../../../pages/LoginPage/LoginPage";
import { AuthShell } from "../../base/AuthShell";
import { AuthField } from "../../base/AuthShell/AuthField";
import { useTranslation } from '../../../hooks/useTranslation';
import { isPublicNext } from "../../../next/public/mode";
import { ContactLink } from "../../../next/public/PublicContact";
import { LinkProblem, LinkSent, SignInErrorLine, SwitchAccount, isLockedOut } from "../../../next/public/SignInExtras";
import { StatusButton, StatusPage } from "../../../next/public/StatusPage";
import s from "../../../next/public/public.module.scss";
import { authAPI } from "../../../backend/api/api";
import { getDefaultLandingPage } from "../../../utils/permissions";
import { authTriggerActions } from "../../../backend/store/reducers/authReducer";

interface IProps {
  formik: LoginFormik;
  isDisabled: boolean;
}

export const Layout: FC<IProps> = ({ formik, isDisabled }) => {
  const { p } = useTranslation('first_page');
  const { p: pGeneral } = useTranslation('general');
  const { t: tNext } = useTranslation();
  const pNext = (k: string) => tNext(`next.${k}`);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleEmail = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("email", e.target.value);
    // The lock is per e-mail: a different address is not held back by it (a server-wide limit answers again if it applies).
    if (isPublicNext() && loginError?.kind === "throttle") dispatch({ type: "SET_LOGIN_ERROR", payload: null });
  };
  const handlePassword = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("password", e.target.value);
  };
  const handleCheck = () => {
    formik.setFieldValue("remember", formik.values.remember ? 0 : 1);
  };

  const [email, setEmail] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [error1, setError] = useState("");
  const [loginByPassword, setLoginByPassword] = useState(true);
  const loginLinkData = useSelector((state: any) => state.authStore.loginLinkData);
  const isFirstRender = useRef(true);
  const next = isPublicNext();
  const loginError = useSelector((state: any) => state.authStore.loginError);
  const signedInUser = useSelector((state: any) => state.authStore.user);
  const search = new URLSearchParams(window.location.search);
  const linkProblem = next && (search.get("link") === "used" || search.get("link") === "expired") ? (search.get("link") as "used" | "expired") : null;
  const linkToken = next ? search.get("token") : null;
  // A fresh page load has no signed-in user in the store yet (the app skips its own session check on a link address), so the link
  // page asks once itself (walkthrough-public #17). A person who is signed in must never be signed out by a link meant for someone else
  // or one that was already used.
  const [me, setMe] = useState<any>(null);
  const sessionUser: any = signedInUser || me;
  const wasSignedIn = !!localStorage.getItem("user") && !!sessionUser;
  // P3.3: a signed-in person opens a link made for someone else. null = asking, "none" = the link belongs to nobody alive.
  const [linkOwner, setLinkOwner] = useState<string | null | "none">(null);
  const [switching, setSwitching] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [recipient, setRecipient] = useState(search.get("email") || "");

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (loginLinkData) {
      dispatch({ type: 'RESET_LOGIN_LINK_DATA' });
      // P3.4: the new look stays here and says what to do next; today's look goes back to the website.
      if (next) setSentCount((n) => n + 1);
      else navigate('/');
    }
  }, [loginLinkData, navigate, dispatch, next]);

  // P3.3: who is the link for? (a first name, or nobody when the link is dead)
  useEffect(() => {
    if (!linkToken || !wasSignedIn) return;
    let live = true;
    authAPI.loginLinkOwner(linkToken)
      .then((r: any) => { if (live) setLinkOwner(r?.data?.first_name || "none"); })
      .catch(() => { if (live) setLinkOwner("none"); });
    return () => { live = false; };
  }, [linkToken, wasSignedIn]);
  useEffect(() => {
    if (!next || !linkToken || !search.get("email") || !localStorage.getItem("user") || signedInUser) return;
    let live = true;
    Promise.resolve().then(() => authAPI.getMe())
      .then((r: any) => { if (live && r?.data?.user) setMe(r.data.user); })
      .catch(() => { /* session over: the "stale flag" check below signs the link in */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, linkToken, signedInUser]);
  // Back to the app without ending the session (the page is loaded afresh so the app knows who is signed in).
  const backToApp = () => { if (signedInUser) navigate(getDefaultLandingPage(signedInUser)); else window.location.assign("/login"); };

  // P2.2: the address typed on registration (or in an e-mail) comes with the link; the cursor goes to the password.
  const urlEmail0 = search.get("email");
  const prefilled = next && !!urlEmail0 && !search.get("token") && !linkProblem;
  useEffect(() => {
    if (prefilled && urlEmail0) formik.setFieldValue("email", urlEmail0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilled, urlEmail0]);

  const continueAsOwner = async () => {
    if (!linkToken) return;
    setSwitching(true);
    // I2: the normal sign-out (unsynced-writes check, saved app data, drafts, admin token, offline flag), then the link is used.
    const email = search.get("email") || "";
    dispatch(authTriggerActions.logoutUserTriggerAC(false, { email, token: linkToken, workspace_id: search.get("workspace_id") }));
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSendLoginLink = async () => {
    setDisabled(true);
    if (!validateEmail(email)) {
      setError(p('invalid_email'));
      setDisabled(false);
      return;
    }
    setError("");
    dispatch(authTriggerActions.linkLoginUserTriggerAC(email));
    setDisabled(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlEmail = urlParams.get("email");
    const token = urlParams.get("token");
    const workspace_id = urlParams.get("workspace_id");
    const mode = urlParams.get("mode");
    // Stage 7A (F9), a deliberate change to today's sign-in page: the e-mail button of "Open <account>" carries
    // `open=<account id>` and `next=<path>`; both go to the sign-in call, which checks them (see the saga).
    const open = urlParams.get("open");
    const next = urlParams.get("next");

    if (mode === "magic") {
      setLoginByPassword(false);
    }

    if (urlEmail && token && !(isPublicNext() && localStorage.getItem("user"))) {
      dispatch(authTriggerActions.tokenLoginUserTriggerAC({ email: urlEmail, token, workspace_id, ...(open && /^\d+$/.test(open) ? { open } : {}), ...(next ? { next } : {}) }));
    }
  }, [dispatch]);

  // I3 (new look): a saved "signed in" flag may be stale (the session ran out). The link must still work: when the session
  // check fails (or nobody is confirmed after a few seconds) the flag is cleared and the link signs in. A confirmed
  // signed-in person gets the switch question instead.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlEmail = urlParams.get("email");
    const token = urlParams.get("token");
    if (!next || !urlEmail || !token || !localStorage.getItem("user") || sessionUser) return;
    const open = urlParams.get("open");
    const nextPath = urlParams.get("next");
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      localStorage.removeItem("user");
      dispatch(authTriggerActions.tokenLoginUserTriggerAC({ email: urlEmail, token, workspace_id: urlParams.get("workspace_id"), ...(open && /^\d+$/.test(open) ? { open } : {}), ...(nextPath ? { next: nextPath } : {}) }));
    };
    window.addEventListener("cy-session-ended", go);
    const timer = setTimeout(go, 4000);
    return () => { done = true; window.removeEventListener("cy-session-ended", go); clearTimeout(timer); };
  }, [next, sessionUser, dispatch]);

  const deadWhileSignedIn = next && !!linkToken && wasSignedIn && linkOwner === "none";
  // The sign-in is being decided (session check, link check or the link signing in): never the plain form with the link left in the address.
  const linkPending = next && !!linkToken && !!search.get("email") && !linkProblem && !(wasSignedIn && linkOwner !== null);

  const sentView = next && sentCount > 0;
  const switchView = next && !!linkToken && wasSignedIn && linkOwner !== null && linkOwner !== "none";
  const heading = deadWhileSignedIn ? tNext("next.public.link.dead_title")
    : linkPending ? tNext("next.public.link.checking")
    : linkProblem
    ? tNext(`next.public.link.${linkProblem}`)
    : sentView ? p('check_email_title')
    : switchView ? tNext("next.public.switch.title", { name: sessionUser?.first_name || "", other: linkOwner })
    : p('lets_sign_in');
  const subheading = deadWhileSignedIn || linkPending || linkProblem || sentView || switchView ? undefined : (loginByPassword ? p('login_subtitle') : p('enter_email_for_link'));
  const toPassword = () => { setSentCount(0); setLoginByPassword(true); if (linkProblem) navigate("/login", { replace: true }); };
  const askAgain = (to: string) => { if (to) { setRecipient(to); dispatch(authTriggerActions.linkLoginUserTriggerAC(to)); } };

  // Walkthrough #3: Enter in a field signs in (or sends the link), like any form; the button is the form's submit button.
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!loginByPassword) {
      if (email && !disabled) { setRecipient(email); handleSendLoginLink(); }
      return;
    }
    if (isDisabled || (next && isLockedOut(loginError))) return;
    formik.handleSubmit();
  };

  // Walkthrough #4/#5: the right password, but nothing to open: the no-account page, or "being reviewed" (a person staff did not approve).
  if (next && (loginError?.kind === "no_account" || loginError?.kind === "waiting")) {
    const none = loginError.kind === "no_account";
    const back = () => dispatch({ type: "SET_LOGIN_ERROR", payload: null });
    return (
      <StatusPage
        art={none ? "none" : "wait"}
        title={none ? tNext("unauth_page.no_workspace_title") : tNext("unauth_page.you_are_registered")}
        actions={<><StatusButton primary onClick={back}>{pNext("public.signin.back")}</StatusButton><ContactLink variant="button">{tNext("landing_nav.contact_us")}</ContactLink></>}
      >
        {none ? tNext("unauth_page.no_workspace_message") : pNext("public.pending_message")}
      </StatusPage>
    );
  }

  return (
    <AuthShell
      icon={deadWhileSignedIn || linkPending || linkProblem ? "link" : sentView ? "mailcheck" : switchView ? "login" : !loginByPassword ? "link" : "login"}
      backTo="/"
      eyebrow={p('login_eyebrow')}
      title={heading}
      subtitle={subheading}
      footnote={p('brand_footnote_login')}
      footer={
        <>
          <p className="authshell-footline">
            {p('no_account')}{" "}
            <Link to="/registration" className="authshell-link">{p('create_account')}</Link>
          </p>
          {/* P2.4, new look only: a visible way to ask for help; opens the one contact form with "I cannot sign in" chosen. */}
          {isPublicNext() && <ContactLink topic="cannot_sign_in">{pNext("public.need_help_signin")}</ContactLink>}
        </>
      }
    >
      {deadWhileSignedIn ? (
        <div className="authshell-stack">
          <p className={s.linkNote} role="status">{tNext("next.public.link.dead_body")}</p>
          <button type="button" className="authshell-cta" onClick={backToApp}>{tNext("next.public.link.dead_continue")}</button>
        </div>
      ) : linkPending ? (
        <div aria-busy="true" />
      ) : linkProblem ? (
        <LinkProblem onPassword={toPassword} onAnother={() => askAgain(recipient)} />
      ) : sentView ? (
        <LinkSent restartKey={sentCount} onAgain={() => askAgain(recipient || email)} onPassword={toPassword} />
      ) : switchView ? (
        <SwitchAccount name={sessionUser?.first_name || ""} other={String(linkOwner)} busy={switching}
          onContinue={continueAsOwner} onStay={backToApp} />
      ) : (
      <form noValidate autoComplete="new-password" onSubmit={submit}>
        <div className="authshell-stack">
          {next && search.get("reset") === "1" && <p className={s.resetDone} role="status">{tNext("next.public.signin.reset_done")}</p>}
          {!loginByPassword ? (
            <>
              <AuthField
                label={pGeneral('email')}
                placeholder={p('enter_your_email')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                error={error1}
              />
              <button
                type="submit"
                className="authshell-cta"
                disabled={!email || disabled}
              >
                {p('send_login_link')}
              </button>
            </>
          ) : (
            <>
              <AuthField
                label={pGeneral('email')}
                placeholder={pGeneral('email')}
                value={formik.values.email}
                error={formik.touched.email && (formik.errors.email as string)}
                onChange={(e) => { formik.setTouched({ ...formik.touched, email: true }); handleEmail(e); }}
              />
              <AuthField
                label={pGeneral('password')}
                placeholder={pGeneral('password')}
                value={formik.values.password}
                error={formik.touched.password && (formik.errors.password as string)}
                onChange={(e) => { formik.setTouched({ ...formik.touched, password: true }); handlePassword(e); }}
                type="password"
                revealable
                autoFocus={prefilled}
              />
              {next && <SignInErrorLine failure={loginError} onDone={() => dispatch({ type: "SET_LOGIN_ERROR", payload: null })} />}
              <div className="authshell-row">
                <label className="authshell-checkbox">
                  <input
                    type="checkbox"
                    checked={!!formik.values.remember}
                    onChange={handleCheck}
                  />
                  <span className="authshell-checkbox__box" aria-hidden="true" />
                  {p('remember_me')}
                </label>
                <Link to="/forgot-password" className="authshell-link">
                  {p('forgot_password_2')}
                </Link>
              </div>
              <button
                type="submit"
                className="authshell-cta"
                disabled={isDisabled || (next && isLockedOut(loginError))}
              >
                {p('sign_in')}
              </button>
            </>
          )}

          <div className="authshell-divider">{p('or')}</div>

          <button
            type="button"
            className="authshell-cta authshell-cta--outline"
            onClick={() => setLoginByPassword(!loginByPassword)}
          >
            {loginByPassword ? p('one_time_login_link') : p('login_with_password')}
          </button>
        </div>
      </form>
      )}
    </AuthShell>
  );
};
